import { useApolloClient } from '@apollo/client';
import { IFeedbackModal } from 'components/layout/FeedbackModal';
import { axiosRestApi } from 'config/axiosRestApi';
import { CF_AUTH_COOKIE } from 'constants/api';
import { useFeedbackModal, useFormManager, useSession } from 'providers';
import { SESSION_STATE } from 'providers/SessionProvider/sessionProvider.constants';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCookie } from 'utils/cookie';
import { getAxiosTooManyRequestsFeedbackModalProps } from 'utils/getAxiosTooManyRequestsFeedbackModalProps';
import { logError } from 'utils/logError';

export const useLogout = () => {
  const [loading, setLoading] = useState(false);

  // Do not destructure apollo client
  // https://github.com/apollographql/apollo-client/issues/4970#issuecomment-1004438065
  const apolloClient = useApolloClient();
  const {
    clearSessionUser,
    toggleCloudflareAutoLogin,
    clearSessionFirebase,
    setSessionState,
  } = useSession();

  const { dirtyFormRef, resetFormManager, confirmationProps } =
    useFormManager();
  const navigate = useNavigate();
  const cloudflareCookie = getCookie(CF_AUTH_COOKIE);
  const { openFeedbackModal } = useFeedbackModal();

  const logoutRequest = useCallback(async () => {
    if (apolloClient.clearStore) {
      await apolloClient.clearStore().catch((error: unknown) => {
        logError({ error });
      });
    }

    await axiosRestApi.post('/logout').catch((error: unknown) => {
      logError({ error });
      const modalProps = getAxiosTooManyRequestsFeedbackModalProps(error);
      if (modalProps) openFeedbackModal(modalProps);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient.clearStore, cloudflareCookie]);

  const handleLogout = useCallback(async () => {
    setLoading(true);
    toggleCloudflareAutoLogin(false);
    setSessionState(SESSION_STATE.LOGGING_OUT);
    try {
      await logoutRequest().then(async () => {
        // clear cookies and storage
        await clearSessionUser().then(() => {
          setLoading(false);
          // navigate to landing page
          navigate('/');
        });
      });
    } catch (error) {
      setLoading(false);
      throw error;
    } finally {
      setSessionState(SESSION_STATE.UNAUTHENTICATED);
    }
  }, [
    clearSessionUser,
    logoutRequest,
    navigate,
    toggleCloudflareAutoLogin,
    setSessionState,
  ]);

  const expireSession = useCallback(async () => {
    setLoading(true);
    setSessionState(SESSION_STATE.LOGGING_OUT);
    try {
      await logoutRequest().then(async () => {
        await clearSessionFirebase();
      });
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
      setSessionState(SESSION_STATE.EXPIRED);
    }
  }, [clearSessionFirebase, logoutRequest, setSessionState]);

  const openLogoutConfirmation = useCallback(() => {
    let feedbackModalOptions: IFeedbackModal = {
      type: 'warning',
      title: 'UNSAVED CHANGES',
      message: 'You have unsaved changes. Would you like to discard them?',
      cancelBtn: {
        label: 'Cancel',
      },
      submitBtn: {
        label: 'Discard',
        onClick: () => {
          handleLogout().catch((error: unknown) => {
            logError({ error });
          });
          resetFormManager();
        },
      },
    };

    if (dirtyFormRef.current === 'saveable' && confirmationProps) {
      const { cancelLabel, discardLabel, onDiscard, additionalOptions } =
        confirmationProps;

      feedbackModalOptions = {
        ...feedbackModalOptions,
        cancelBtn: {
          label: cancelLabel,
        },
        submitBtn: {
          label: discardLabel,
          onClick: onDiscard,
        },
        additionalOptions,
      };
    }

    openFeedbackModal(feedbackModalOptions);
  }, [
    dirtyFormRef,
    confirmationProps,
    openFeedbackModal,
    handleLogout,
    resetFormManager,
  ]);

  const logout = useCallback(() => {
    if (dirtyFormRef.current) {
      openLogoutConfirmation();
      return;
    }
    handleLogout().catch((error: unknown) => {
      logError({ error });
    });
  }, [dirtyFormRef, handleLogout, openLogoutConfirmation]);

  return { logout, handleLogout, loading, expireSession };
};
